import React, { useState, useEffect, useCallback } from "react";
import api from "@/services/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Users, Search, Loader2, Database, Settings2, Phone } from "lucide-react";

const fmt = (n) => `$${Math.round(n || 0).toLocaleString("es-CO")}`;

const ESTADO_STYLE = {
  prospecto: "bg-gray-100 text-gray-700",
  primera_compra: "bg-blue-100 text-blue-800",
  recurrente: "bg-green-100 text-green-800",
  alto_valor: "bg-amber-100 text-amber-800",
  inactivo: "bg-red-100 text-red-700",
  reactivado: "bg-purple-100 text-purple-800",
};

const ESTADO_LABEL = {
  prospecto: "Prospecto", primera_compra: "1ª Compra", recurrente: "Recurrente",
  alto_valor: "Alto Valor", inactivo: "Inactivo", reactivado: "Reactivado",
};

export default function AdminClientes() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("");
  const [perfil, setPerfil] = useState(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [crmConfig, setCrmConfig] = useState(null);
  const [migrando, setMigrando] = useState(false);

  const cargar = useCallback((query = "", estado = "") => {
    const params = new URLSearchParams();
    if (query) params.set("q", query);
    if (estado) params.set("estado", estado);
    api.get(`/crm/clientes?${params}`).then((r) => setData(r.data)).catch(() => toast.error("Error cargando clientes"));
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const buscar = (e) => {
    e.preventDefault();
    cargar(q, filtro);
  };

  const verPerfil = (customerId) => {
    api.get(`/crm/clientes/${customerId}`).then((r) => setPerfil(r.data)).catch(() => toast.error("Error cargando perfil"));
  };

  const migrar = async () => {
    setMigrando(true);
    try {
      const r = await api.post("/crm/migrar");
      toast.success(`Migración completa: ${r.data.clientes_creados} creados, ${r.data.clientes_actualizados} actualizados de ${r.data.ordenes_procesadas} órdenes`);
      cargar(q, filtro);
    } catch {
      toast.error("Error en migración");
    } finally {
      setMigrando(false);
    }
  };

  const abrirConfig = () => {
    api.get("/crm/config").then((r) => { setCrmConfig(r.data); setConfigOpen(true); });
  };

  const guardarConfig = async () => {
    await api.put("/crm/config", crmConfig);
    toast.success("Parámetros actualizados");
    setConfigOpen(false);
    cargar(q, filtro);
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-torta-brown" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-clientes-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl text-torta-dark flex items-center gap-2"><Users className="w-7 h-7" /> Clientes (CRM)</h1>
          <p className="text-torta-muted text-sm">Identificados por celular · customer_id único</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="config-crm-btn" onClick={abrirConfig}><Settings2 className="w-4 h-4 mr-1" /> Ciclo de vida</Button>
          <Button data-testid="migrar-btn" onClick={migrar} disabled={migrando} className="bg-torta-brown hover:bg-torta-brown/90">
            {migrando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Database className="w-4 h-4 mr-1" />} Migrar histórico
          </Button>
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {Object.entries(ESTADO_LABEL).map(([k, l]) => (
          <button key={k} data-testid={`filtro-${k}`} onClick={() => { const nuevo = filtro === k ? "" : k; setFiltro(nuevo); cargar(q, nuevo); }}
            className={`rounded-xl p-3 text-center border transition-colors ${filtro === k ? "border-torta-brown bg-torta-cream" : "border-torta-sand bg-white"}`}>
            <p className="text-xl font-bold text-torta-dark">{data.resumen?.[k] || 0}</p>
            <p className="text-[10px] text-torta-muted">{l}</p>
          </button>
        ))}
      </div>

      <form onSubmit={buscar} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-torta-muted" />
          <Input data-testid="buscar-cliente-input" className="pl-9" placeholder="Buscar por nombre, celular o customer_id..." value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <Button type="submit" data-testid="buscar-cliente-btn" className="bg-torta-brown hover:bg-torta-brown/90">Buscar</Button>
      </form>

      <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
        {data.clientes.length === 0 ? (
          <p className="text-center py-10 text-torta-muted text-sm">No hay clientes aún. Usa "Migrar histórico" para crear clientes desde las órdenes existentes.</p>
        ) : (
          <div className="divide-y divide-torta-sand/50">
            {data.clientes.map((c) => (
              <button key={c.customer_id} data-testid={`cliente-row-${c.customer_id}`} onClick={() => verPerfil(c.customer_id)}
                className="w-full text-left px-4 py-3 hover:bg-torta-cream/40 transition-colors flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-[150px]">
                  <p className="font-semibold text-torta-dark text-sm">{c.nombre || "Sin nombre"}</p>
                  <p className="text-xs text-torta-muted flex items-center gap-1"><Phone className="w-3 h-3" /> {c.phone_normalized} · {c.customer_id}</p>
                </div>
                <Badge className={`border-0 ${ESTADO_STYLE[c.estado]}`}>{ESTADO_LABEL[c.estado]}</Badge>
                <div className="text-right">
                  <p className="text-sm font-bold text-torta-dark">{fmt(c.facturacion_total)}</p>
                  <p className="text-[10px] text-torta-muted">{c.total_ordenes} {c.total_ordenes === 1 ? "orden" : "órdenes"}</p>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Perfil 360 */}
      <Dialog open={!!perfil} onOpenChange={(v) => !v && setPerfil(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="perfil-cliente-dialog">
          {perfil && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {perfil.cliente.nombre || "Sin nombre"}
                  <Badge className={`border-0 ${ESTADO_STYLE[perfil.cliente.estado]}`}>{ESTADO_LABEL[perfil.cliente.estado]}</Badge>
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <InfoBox l="Customer ID" v={perfil.cliente.customer_id} />
                  <InfoBox l="Celular" v={perfil.cliente.phone_normalized} />
                  <InfoBox l="Órdenes" v={perfil.cliente.total_ordenes} />
                  <InfoBox l="Facturación" v={fmt(perfil.cliente.facturacion_total)} />
                  <InfoBox l="Ticket promedio" v={fmt(perfil.ticket_promedio)} />
                  <InfoBox l="Canal de origen" v={perfil.cliente.canal_adquisicion || "—"} />
                  <InfoBox l="Primera compra" v={(perfil.cliente.fecha_primera_compra || "—").slice(0, 10)} />
                  <InfoBox l="Última compra" v={(perfil.cliente.fecha_ultima_compra || "—").slice(0, 10)} />
                </div>
                {perfil.cliente.vendedor_origen_nombre && <p className="text-xs text-torta-muted">Vendedor de origen: <strong>{perfil.cliente.vendedor_origen_nombre}</strong></p>}
                {perfil.productos_favoritos.length > 0 && (
                  <div>
                    <p className="font-semibold text-torta-dark mb-1">Productos favoritos</p>
                    <div className="flex flex-wrap gap-1.5">
                      {perfil.productos_favoritos.map((f) => <Badge key={f.producto} variant="outline">{f.producto} ×{f.unidades}</Badge>)}
                    </div>
                  </div>
                )}
                <div>
                  <p className="font-semibold text-torta-dark mb-1">Historial de órdenes ({perfil.ordenes.length})</p>
                  <div className="space-y-1.5 max-h-56 overflow-y-auto">
                    {perfil.ordenes.map((o) => (
                      <div key={o.id} className="border border-torta-sand rounded-lg px-3 py-2 flex justify-between items-center">
                        <div>
                          <p className="font-medium text-torta-dark">{o.numero_pedido} <span className="text-[10px] text-torta-muted capitalize">· {(o.origen || "").replace("_", " ")}</span></p>
                          <p className="text-[10px] text-torta-muted">{String(o.fecha_creacion).slice(0, 10)} · {o.vendedor_nombre}{o.embajador_nombre ? ` · Emb: ${o.embajador_nombre}` : ""}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-torta-dark">{fmt(o.total)}</p>
                          <p className="text-[10px] capitalize text-torta-muted">{o.estado}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Config ciclo de vida */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-sm" data-testid="config-crm-dialog">
          <DialogHeader><DialogTitle>Parámetros del ciclo de vida</DialogTitle></DialogHeader>
          {crmConfig && (
            <div className="space-y-3">
              <div><Label className="text-xs">Días sin comprar para marcar INACTIVO</Label>
                <Input type="number" data-testid="dias-inactivo-input" value={crmConfig.dias_inactivo} onChange={(e) => setCrmConfig({ ...crmConfig, dias_inactivo: parseInt(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs">Compras mínimas para RECURRENTE</Label>
                <Input type="number" value={crmConfig.compras_recurrente} onChange={(e) => setCrmConfig({ ...crmConfig, compras_recurrente: parseInt(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs">Facturación acumulada para ALTO VALOR ($)</Label>
                <Input type="number" value={crmConfig.alto_valor_facturacion} onChange={(e) => setCrmConfig({ ...crmConfig, alto_valor_facturacion: parseInt(e.target.value) || 0 })} /></div>
              <div><Label className="text-xs">Días que dura el estado REACTIVADO</Label>
                <Input type="number" value={crmConfig.dias_ventana_reactivado} onChange={(e) => setCrmConfig({ ...crmConfig, dias_ventana_reactivado: parseInt(e.target.value) || 0 })} /></div>
              <Button data-testid="guardar-config-crm-btn" onClick={guardarConfig} className="w-full bg-torta-brown hover:bg-torta-brown/90">Guardar</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const InfoBox = ({ l, v }) => (
  <div className="bg-torta-cream/50 rounded-lg px-3 py-2">
    <p className="text-[10px] text-torta-muted uppercase">{l}</p>
    <p className="font-semibold text-torta-dark text-sm">{v}</p>
  </div>
);
